// The frame the sign-in screen sits in: the Clavian lockup — the eclipse mark — above one clean card, centred
// in the nebula (BRAND.md §6.1, ADR-0060: a sign-in page is a screen nobody works on, so it may have the sky;
// in the light theme the sky paints nothing and the ground stays plain). The same frame as the console's.

import { Link } from '@tanstack/react-router';
import { lazy, Suspense, type ReactNode } from 'react';

import { Mark } from '../shell/Mark';

export interface AuthLayoutProps {
  /** The `<h1>` inside the card. */
  readonly title: string;
  /** One muted line under the title, on the first step. Absent on later steps. */
  readonly subtitle?: ReactNode;
  /** A muted "who this is for" note below the card. */
  readonly footer?: ReactNode;
  /** Whether the lockup links home. Off on a one-task page that should not be left (a PC's sign-in window). */
  readonly homeLink?: boolean;
  readonly children: ReactNode;
}

/** The nebula, loaded on first use in its own chunk: only the sign-in page pays for it. */
const Nebula = lazy(() => import('@clavian/ui-web/nebula').then((module) => ({ default: module.Nebula })));

export function AuthLayout({ title, subtitle, footer, homeLink = true, children }: AuthLayoutProps): ReactNode {
  const lockup = (
    <>
      <Mark size={32} variant="eclipse" />
      <span className="pw-auth__wordmark" aria-hidden="true">
        Clavian<span className="pw-wordmark__stop">.</span>
      </span>
    </>
  );
  return (
    <main className="pw-auth" id="pw-main">
      <Suspense fallback={null}>
        <Nebula />
      </Suspense>
      <div className="pw-auth__panel">
        {homeLink ? (
          <Link to="/" className="pw-auth__brand" aria-label="Clavian">
            {lockup}
          </Link>
        ) : (
          <div className="pw-auth__brand" role="img" aria-label="Clavian">
            {lockup}
          </div>
        )}
        <div className="pw-auth__card">
          <div className="pw-auth__head">
            {/* Focusable from script, so a step change can move focus to what the page now says. */}
            <h1 tabIndex={-1}>{title}</h1>
            {subtitle === undefined ? null : <p className="pw-auth__subtitle pw-muted">{subtitle}</p>}
          </div>
          {children}
        </div>
        {footer === undefined ? null : <p className="pw-auth__footer pw-subtle">{footer}</p>}
      </div>
    </main>
  );
}
